import {
  useConnectionStatusContext,
  useMarketAccountsContext,
  useMarketsContext,
  useSecuritiesContext,
  useUnifiedLiquidityContext,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { usePermittedTradableMarketAccountsSet } from '../../../../../hooks/usePermittedTradableMarketAccountsSet';
import { useSubAccountAllocationsForRestrictedTrading } from '../../../../../hooks/useSubAccountAllocationsForRestrictedTrading';
import { useDefaultAggregation } from '../../../../../providers/DefaultAggregationContext';
import { useTradingSettings } from '../../../../../providers/TradingSettingsContext';
import { useMarketSelectorAggregationPresets } from '../../../../MarketSelector/presets/useMarketSelectorAggregationPresets';
import {
  buildTradingSymbolDefaultPanelMarkets,
  type TradingSymbolPanelMarkets,
} from '../../../../MarketSelector/tradingSymbolDefaultMarkets';

/**
 * Builds the Deep Dive panel fields to apply when the user changes symbols.
 * Deep Dive uses default aggregations but keeps unified-liquidity markets out of the selector options.
 */
export function useBuildDeepDiveSymbolChangePanelParams(): (nextSymbol: string) => TradingSymbolPanelMarkets {
  const { useNewMarketSelector } = useTradingSettings();
  const { defaultAggregationId } = useDefaultAggregation();
  const { aggregationPresets } = useMarketSelectorAggregationPresets();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketAccountsByMarket, marketAccountsByName } = useMarketAccountsContext();
  const { marketsByName } = useMarketsContext();
  const { unifiedLiquidityTokenBySymbol } = useUnifiedLiquidityContext();
  const { marketCredentialsWithConnectionStatusByMarketAccountName } = useConnectionStatusContext();
  const { enableAccountSegregationWithRestrictedTrading } = useFeatureFlag();
  const subAccountAllocations = useSubAccountAllocationsForRestrictedTrading();
  const permittedMarketAccountNames = usePermittedTradableMarketAccountsSet({
    subAccountAllocations,
    combineAllMarketAccounts: enableAccountSegregationWithRestrictedTrading,
  });

  return useCallback(
    (nextSymbol: string) => {
      const security = securitiesBySymbol.get(nextSymbol);

      return buildTradingSymbolDefaultPanelMarkets({
        nextSymbol,
        security,
        securitiesBySymbol,
        useNewMarketSelector,
        defaultAggregationId,
        aggregationPresets,
        marketAccountsByMarket,
        marketsByName,
        marketAccountsByName,
        unifiedLiquidityTokenBySymbol,
        enableAccountSegregationWithRestrictedTrading,
        permittedMarketAccountNames: enableAccountSegregationWithRestrictedTrading ? permittedMarketAccountNames : null,
        marketCredentialsWithConnectionStatusByMarketAccountName,
        includeUnifiedLiquidityMarkets: false,
      });
    },
    [
      securitiesBySymbol,
      useNewMarketSelector,
      defaultAggregationId,
      aggregationPresets,
      marketAccountsByMarket,
      marketsByName,
      marketAccountsByName,
      unifiedLiquidityTokenBySymbol,
      enableAccountSegregationWithRestrictedTrading,
      permittedMarketAccountNames,
      marketCredentialsWithConnectionStatusByMarketAccountName,
    ]
  );
}
